import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Lock, CreditCard, ShieldCheck } from "@phosphor-icons/react";
import api from "../lib/api";
import { flag, fmtData, fmtEUR, countryName } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PaymentLogos from "../components/PaymentLogos";
import LegalNotice from "../components/LegalNotice";

export default function Checkout() {
  const { t, i18n } = useTranslation();
  const { planId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);
  const [email, setEmail] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/plans/${planId}`).then(({ data }) => setPlan(data)).catch(() => nav("/plans"));
  }, [planId, nav]);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  const pay = async () => {
    if (!email) { setError(t("checkout.emailLabel")); return; }
    if (!accepted) {
      setError(t("checkout.termsRequired", "Please accept the Terms & Conditions and Privacy Policy to continue."));
      return;
    }
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        plan_id: planId,
        origin_url: window.location.origin,
        email,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || t("common.error"));
      setLoading(false);
    }
  };

  if (!plan) return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-2xl mx-auto px-6 py-24 text-ink-500">{t("common.loading")}</div>
    </div>
  );

  return (
    <div className="min-h-screen" data-testid="checkout-page">
      <Header />
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-14">
        <div className="overline mb-3">{t("checkout.title")}</div>
        <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tight mb-10">{t("checkout.title")}</h1>

        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-5">
          {/* Summary */}
          <div className="card-soft p-8">
            <div className="overline mb-4">{t("checkout.planSummary")}</div>
            <div className="flex items-start gap-4 mb-8 pb-8 border-b border-ink-200">
              <div className="text-5xl">{flag(plan.country_iso2)}</div>
              <div className="flex-1">
                <div className="font-display font-bold text-2xl tracking-tight">{plan.name}</div>
                <div className="text-ink-700 mt-1">{countryName(plan.country_iso2, i18n.language, plan.country)}</div>
              </div>
              <div className="font-display font-bold text-3xl tracking-tight">{fmtEUR(plan.retail_price)}</div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-cobalt-50 rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-cobalt-600">{t("common.data")}</div>
                <div className="font-display font-bold text-xl mt-1">{fmtData(plan.data_mb)}</div>
              </div>
              <div className="bg-coral-soft/60 rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-coral-deep">{t("common.days")}</div>
                <div className="font-display font-bold text-xl mt-1">{plan.days}</div>
              </div>
              <div className="bg-mint-soft rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-mint">{t("common.country")}</div>
                <div className="font-display font-bold text-xl mt-1">{plan.country_iso2}</div>
              </div>
            </div>
          </div>

          {/* Pay */}
          <div className="card-soft p-8 h-fit">
            <div className="overline mb-2">{t("checkout.totalDue")}</div>
            <div className="font-display font-bold text-5xl tracking-tight mb-6">{fmtEUR(plan.retail_price)}</div>

            <PaymentLogos size={22} className="mb-6" testid="checkout-payment-logos" />

            <label className="block mb-5">
              <span className="text-sm font-semibold text-ink-900">{t("checkout.emailLabel")}</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!!user}
                placeholder="you@email.com"
                className="input-soft mt-2 disabled:bg-ink-100"
                data-testid="checkout-email-input"
              />
              <span className="text-xs text-ink-500 mt-1.5 block">{t("checkout.emailHelp")}</span>
            </label>

            {/* Terms acceptance — required */}
            <label className="flex items-start gap-3 mb-5 cursor-pointer select-none" data-testid="checkout-terms-label">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-cobalt-500 flex-shrink-0"
                data-testid="checkout-accept-terms"
              />
              <span className="text-xs text-ink-700 leading-relaxed">
                {t("checkout.acceptPre", "I have read and accept the")}{" "}
                <Link to="/legal/terms" target="_blank" className="font-semibold text-cobalt-600 hover:underline">
                  {t("footer.terms")}
                </Link>{" "}
                {t("common.and", "and")}{" "}
                <Link to="/legal/privacy" target="_blank" className="font-semibold text-cobalt-600 hover:underline">
                  {t("footer.privacy")}
                </Link>{". "}
                {t("checkout.acceptPost", "I authorise BlueRoam Ltd to process this payment.")}
              </span>
            </label>

            {error && <div className="text-sm text-destructive mb-3 bg-red-50 rounded-xl p-3" data-testid="checkout-error">{error}</div>}

            <button
              onClick={pay}
              disabled={loading || !accepted}
              className="btn-primary w-full text-base disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="checkout-pay-button"
            >
              {loading ? t("checkout.processing") : <><CreditCard size={18} weight="bold" /> {t("checkout.payNow")}</>}
            </button>
            <div className="mt-3 flex items-center gap-2 text-xs text-ink-500">
              <Lock size={12} weight="bold" /> {t("checkout.securedByNp", "Payments securely processed by NeptunePay · 256-bit TLS")}
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs text-ink-500">
              <ShieldCheck size={14} className="text-mint" /> {t("checkout.terms")}
            </div>

            <div className="mt-6">
              <LegalNotice testid="checkout-legal-notice" />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
